import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock, LockOpen, ShieldCheck } from 'lucide-react';
import { api } from '@/lib/api';

interface Row { class_id: number; class: string; subject_id: number; subject: string; teacher: string | null; students: number; entries: number; locked_entries: number; state: 'open' | 'locked' | 'partly'; last_change: string | null }
const STATE: Record<Row['state'], [string, string]> = { open: ['Open', 'bg-amber-50 text-amber-800'], partly: ['Partly locked', 'bg-blue-50 text-blue-700'], locked: ['Locked', 'bg-green-50 text-green-700'] };
const errText = (e: any) => e?.response?.data?.message ?? 'Something went wrong.';

/**
 * Uploaded scores, class by class: lock them so nobody (teachers included) can change them, or unlock to let a
 * teacher correct something. Every lock and unlock is written to the audit log.
 */
export function ScoreLocksPage() {
  const qc = useQueryClient();
  const terms = useQuery({ queryKey: ['terms-all'], queryFn: () => api.get('/school/terms').then((r) => (r.data?.data ?? []) as { id: number; name: string; is_current: boolean; academic_session_id: number }[]) });
  const [termId, setTermId] = useState<number | ''>('');
  const tid = termId || terms.data?.find((t) => t.is_current)?.id;
  const q = useQuery({ queryKey: ['score-locks', tid], enabled: !!tid, queryFn: () => api.get('/teacher/scores/locks', { params: { term_id: tid } }).then((r) => r.data?.data as { term: { name: string }; rows: Row[] }) });
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [filter, setFilter] = useState<'all' | Row['state']>('all');

  const byClass = useMemo(() => {
    const m = new Map<number, { class: string; rows: Row[] }>();
    (q.data?.rows ?? []).filter((r) => filter === 'all' || r.state === filter).forEach((r) => { if (!m.has(r.class_id)) m.set(r.class_id, { class: r.class, rows: [] }); m.get(r.class_id)!.rows.push(r); });
    return [...m.entries()];
  }, [q.data, filter]);
  const all = q.data?.rows ?? [];
  const count = (s: Row['state']) => all.filter((r) => r.state === s).length;

  const run = async (key: string, fn: () => Promise<any>) => {
    setBusy(key); setMsg(null);
    try { const r = await fn(); setMsg({ ok: true, text: r.data?.message ?? 'Done.' }); qc.invalidateQueries({ queryKey: ['score-locks'] }); }
    catch (e) { setMsg({ ok: false, text: errText(e) }); } finally { setBusy(null); }
  };
  const lockOne = (r: Row, locked: boolean) => run(`${r.class_id}-${r.subject_id}`, () => api.post('/teacher/scores/lock', { class_id: r.class_id, subject_id: r.subject_id, term_id: tid, locked }));
  const lockClass = (classId: number, locked: boolean) => run(`c${classId}`, () => api.post('/teacher/scores/lock-class', { class_id: classId, term_id: tid, locked }));

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5 sm:py-6 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div><p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Results</p><h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900">Score locks</h1>
          <p className="text-sm text-slate-500">Lock uploaded scores so nobody can change them. Unlock when a teacher needs to correct something.</p></div>
        <select value={termId} onChange={(e) => setTermId(e.target.value ? Number(e.target.value) : '')} className="border border-slate-300 rounded-xl px-3 py-2.5 text-sm bg-white">
          <option value="">Current term</option>
          {(terms.data ?? []).map((t) => <option key={t.id} value={t.id}>{t.name}{t.is_current ? ' (current)' : ''}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        {(['open', 'partly', 'locked'] as const).map((s) => (
          <button key={s} onClick={() => setFilter(filter === s ? 'all' : s)} className={`text-left bg-white rounded-2xl border p-3 sm:p-4 ${filter === s ? 'border-[#0A2540] ring-2 ring-[#0A2540]/10' : 'border-slate-200'}`}>
            <p className="text-xs text-slate-500">{STATE[s][0]}</p><p className="text-2xl font-extrabold text-slate-900">{count(s)}</p><p className="text-[11px] text-slate-400">subject sheets</p>
          </button>
        ))}
      </div>
      {msg && <p className={`text-sm rounded-xl px-3 py-2 ${msg.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'}`}>{msg.text}</p>}

      {q.isLoading ? <p className="text-sm text-slate-500">Loading…</p> : !byClass.length ? (
        <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center"><ShieldCheck className="w-8 h-8 text-slate-300 mx-auto" /><p className="text-sm text-slate-500 mt-2">{all.length ? 'Nothing in this filter.' : 'No scores have been uploaded this term yet.'}</p></div>
      ) : byClass.map(([cid, g]) => {
        const allLocked = g.rows.every((r) => r.state === 'locked');
        return (
          <section key={cid} className="bg-white rounded-2xl border border-slate-200">
            <div className="flex items-center justify-between gap-3 px-4 pt-3.5 pb-2">
              <h2 className="font-bold text-slate-900">{g.class}</h2>
              <button disabled={busy === `c${cid}`} onClick={() => lockClass(cid, !allLocked)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold disabled:opacity-40 ${allLocked ? 'border border-slate-300 text-slate-700' : 'bg-[#0A2540] text-white'}`}>
                {allLocked ? <><LockOpen className="w-3.5 h-3.5" />Unlock all</> : <><Lock className="w-3.5 h-3.5" />Lock all subjects</>}
              </button>
            </div>
            <ul className="divide-y divide-slate-100">
              {g.rows.map((r) => {
                const k = `${r.class_id}-${r.subject_id}`;
                return (
                  <li key={k} className="px-4 py-2.5 flex items-center gap-3">
                    <div className="min-w-0 flex-1"><p className="text-sm font-semibold text-slate-900">{r.subject}</p>
                      <p className="text-xs text-slate-500 truncate">{r.teacher ?? 'No teacher assigned'} · {r.students} student{r.students === 1 ? '' : 's'}{r.last_change ? ` · last change ${new Date(r.last_change).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : ''}</p></div>
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${STATE[r.state][1]}`}>{STATE[r.state][0]}</span>
                    <button disabled={busy === k} onClick={() => lockOne(r, r.state !== 'locked')} className="shrink-0 p-2 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-40" title={r.state === 'locked' ? 'Unlock' : 'Lock'}>
                      {r.state === 'locked' ? <LockOpen className="w-4 h-4 text-slate-600" /> : <Lock className="w-4 h-4 text-slate-600" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
